import { getFunctions, httpsCallable } from "firebase/functions";

const TRANSLATION_CACHE_KEY = "rentduo_translation_cache_v1";

const SPANISH_MARKERS = [
  " el ",
  " la ",
  " los ",
  " las ",
  " de ",
  " que ",
  " con ",
  " para ",
  " por ",
  " una ",
  " un ",
  " no ",
  " muy ",
  " pero ",
  " alquiler",
  " renta",
  " arrendador",
  " inquilino",
  " propiedad",
  " apartamento",
  " mantenimiento",
  " gracias",
  " bueno",
  " mala",
  " excelente",
  " español",
];

const ENGLISH_MARKERS = [
  " the ",
  " and ",
  " with ",
  " for ",
  " from ",
  " rent ",
  " rental ",
  " landlord",
  " tenant",
  " property",
  " apartment",
  " maintenance",
  " deposit",
  " good",
  " bad",
  " excellent",
  " responsive",
];

const DICTIONARY = {
  es: {
    landlord: "propietario",
    tenant: "inquilino",
    property: "propiedad",
    apartment: "apartamento",
    rent: "renta",
    rental: "renta",
    maintenance: "mantenimiento",
    deposit: "depósito",
    clean: "limpio",
    safe: "seguro",
    responsive: "respondió bien",
    good: "bueno",
    bad: "malo",
    excellent: "excelente",
    noisy: "ruidoso",
    quiet: "tranquilo",
    communication: "comunicación",
    was: "fue",
    is: "es",
    very: "muy",
    not: "no",
    and: "y",
    but: "pero",
  },
  en: {
    propietario: "landlord",
    arrendador: "landlord",
    inquilino: "tenant",
    propiedad: "property",
    apartamento: "apartment",
    renta: "rent",
    alquiler: "rent",
    mantenimiento: "maintenance",
    depósito: "deposit",
    deposito: "deposit",
    limpio: "clean",
    seguro: "safe",
    buena: "good",
    bueno: "good",
    malo: "bad",
    mala: "bad",
    excelente: "excellent",
    ruidoso: "noisy",
    tranquilo: "quiet",
    comunicación: "communication",
    comunicacion: "communication",
    fue: "was",
    es: "is",
    muy: "very",
    no: "not",
    y: "and",
    pero: "but",
  },
};

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(TRANSLATION_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeCache(cache) {
  try {
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Cache is optional.
  }
}

function markerScore(text, markers) {
  const normalized = ` ${String(text || "").toLowerCase()} `;
  return markers.reduce((score, marker) => score + (normalized.includes(marker) ? 1 : 0), 0);
}

export function detectTextLanguage(text = "") {
  const value = String(text || "").trim();
  if (!value) return "unknown";

  if (/[áéíóúñü¿¡]/i.test(value)) return "es";

  const spanishScore = markerScore(value, SPANISH_MARKERS);
  const englishScore = markerScore(value, ENGLISH_MARKERS);

  if (spanishScore > englishScore) return "es";
  if (englishScore > spanishScore) return "en";
  return "en";
}

function fallbackTranslate(text = "", targetLanguage = "en") {
  const dictionary = DICTIONARY[targetLanguage] || {};
  let changed = false;
  const translated = String(text || "").replace(/\b[\wáéíóúñüÁÉÍÓÚÑÜ]+\b/g, (word) => {
    const lower = word.toLowerCase();
    const next = dictionary[lower];
    if (!next) return word;
    changed = true;
    return /^[A-Z]/.test(word) ? next.charAt(0).toUpperCase() + next.slice(1) : next;
  });

  if (changed) return translated;
  return targetLanguage === "es"
    ? `[Traducción aproximada] ${text}`
    : `[Approximate translation] ${text}`;
}

export async function translateText(text = "", targetLanguage = "en") {
  const value = String(text || "").trim();
  const target = targetLanguage === "es" ? "es" : "en";
  if (!value) return "";

  const cacheKey = `${target}:${value}`;
  const cache = readCache();
  if (cache[cacheKey]) return cache[cacheKey];

  if (window.RentDuoTranslateService?.translateText) {
    const serviceResult = await window.RentDuoTranslateService.translateText(value, target);
    if (serviceResult) {
      cache[cacheKey] = serviceResult;
      writeCache(cache);
      return serviceResult;
    }
  }

  try {
    const functions = getFunctions();
    const translateTextCallable = httpsCallable(functions, "translateText");
    const response = await translateTextCallable({
      text: value,
      targetLanguage: target,
    });
    const translated =
      response?.data?.translatedText ||
      response?.data?.translated ||
      response?.data?.text ||
      "";

    if (translated) {
      cache[cacheKey] = translated;
      writeCache(cache);
      return translated;
    }
  } catch (error) {
    console.error("RentDuo translation callable failed:", {
      code: error?.code,
      message: error?.message,
      details: error?.details,
    });
    throw new Error(error?.message || "Translation failed.");
  }

  const fallback = fallbackTranslate(value, target);
  cache[cacheKey] = fallback;
  writeCache(cache);
  return fallback;
}
